'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

import { AppUiVisual } from '../../app-ui-visual'
import {
  client,
  CURRENT_YEAR,
  HOURS_DAY_TARGET,
  HOURS_EMPLOYEE,
  PHONE_HOURS,
  phoneDayLabel,
  teamMember,
  weekdayIndexOf,
} from '../../demo-data'
import { PhoneFrame } from '../../frames'
import { activityLabel, useVisualCopy, type VisualCopy } from '../../visual-copy'
import styles from '../mobile-visual.module.css'
import { useScenario } from '../use-scenario'
import { Icon, StatusBar, TabBar } from './chrome'

const urenWeek = (t: VisualCopy) =>
  PHONE_HOURS.weekDays.map((day) => {
    const index = weekdayIndexOf(day)
    const weekend = index >= 5
    return {
      d: t.weekdaysShort[index][0].toUpperCase(),
      n: String(day),
      u: weekend ? '\u2013' : HOURS_DAY_TARGET.replace(/^0/, ''),
    }
  })
const UREN_SEL_DAY = PHONE_HOURS.weekDays.indexOf(PHONE_HOURS.selectedDay)

const UREN_QUICK = PHONE_HOURS.quickSteps

const BOOKING = PHONE_HOURS.booking

/** Small labeled field row (24px icon tile + uppercase label + value) used in the uren form. */
function FormRow({
  icon,
  label,
  value,
  valueColor,
  background,
  last,
}: {
  icon: ReactNode
  label: string
  value: string
  valueColor?: string
  background?: string
  last?: boolean
}) {
  return (
    <div
      className={clsx('-mx-1.5 flex items-center gap-2.5 rounded-[7px] px-1.5 py-[7px]', styles.tone)}
      style={{ borderBottom: last ? 'none' : '1px solid var(--mv-hairline)', background: background ?? 'transparent' }}
    data-sentry-component="FormRow" data-sentry-source-file="uren-phone.tsx">
      <span
        className="flex size-6 shrink-0 items-center justify-center rounded-[7px]"
        style={{ background: 'var(--mv-icon-bg)', color: 'var(--mv-text2)' }}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[8px] font-bold tracking-[0.03em]" style={{ color: 'var(--mv-text2)' }}>
          {label}
        </span>
        <span className="truncate text-[11px] font-semibold" style={{ color: valueColor ?? 'var(--mv-text)' }}>
          {value}
        </span>
      </div>
    </div>
  )
}

export function UrenPhone({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(13, { intervalMs: 1000 })
  const step = beat ?? 8

  const formOpen = step >= 3 && step < 10
  const chosen = step >= 4
  const booked = step >= 10
  const amount = step >= 7 ? BOOKING.corrected : step >= 6 ? BOOKING.typed : '00:00'
  const activity = step >= 6 ? BOOKING.activity : '—'
  const monthTotal = booked ? PHONE_HOURS.monthAfter : PHONE_HOURS.monthBefore

  const days: {
    label: string
    entries: { klant: string; act: string; uur: string; hl: boolean }[]
    empty?: boolean
  }[] = [
    ...PHONE_HOURS.days.map((day) => ({
      label: phoneDayLabel(day.day),
      entries: day.entries.map((entry) => ({
        klant: client(entry.nr).name,
        act: activityLabel(t, entry.activity) ?? entry.activity,
        uur: entry.hours,
        hl: false,
      })),
    })),
    // The day being booked on: empty until the entry lands, then holding it — highlighted for a
    // beat, so you see where what you just typed ended up.
    booked
      ? {
          label: phoneDayLabel(BOOKING.day),
          entries: [{ klant: client(BOOKING.nr).name, act: BOOKING.activity, uur: BOOKING.hours, hl: step < 12 }],
          empty: false,
        }
      : { label: phoneDayLabel(BOOKING.day), entries: [], empty: true },
  ]

  const briefcase = (
    <Icon size={13}>
      <rect x="3.5" y="7.5" width="17" height="12" rx="2.5" />
      <path d="M8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5M3.5 12.5h17" />
    </Icon>
  )

  return (
    <AppUiVisual label={t.screen.mobile.captionHours} className={className} data-sentry-element="AppUiVisual" data-sentry-component="UrenPhone" data-sentry-source-file="uren-phone.tsx">
      <PhoneFrame screenClassName={clsx(styles.root, styles.phoneScreen)} data-sentry-element="PhoneFrame" data-sentry-source-file="uren-phone.tsx">
        <div
          ref={ref}
          className={clsx(styles.root, 'relative flex h-full flex-col')}
          style={{ background: 'var(--mv-scr)', color: 'var(--mv-text)' }}
        >
          <StatusBar detailOpen={formOpen} title={formOpen ? t.screen.mobile.editEntry : t.screen.hours.title} data-sentry-element="StatusBar" data-sentry-source-file="uren-phone.tsx" />

          <div className="relative min-h-0 flex-1 px-2 pt-0.5 pb-1.5">
            {/* List: week strip + day entries */}
            <div className={clsx('flex h-full flex-col gap-1.5', styles.fade)} style={{ opacity: formOpen ? 0 : 1 }}>
              <div
                className="flex shrink-0 flex-col gap-1.5 rounded-[11px] px-2.5 py-2"
                style={{ background: 'var(--mv-card)' }}
              >
                <div
                  className="flex items-center justify-between text-[7.5px] font-semibold"
                  style={{ color: 'var(--mv-text2)' }}
                >
                  <span className="rounded-full px-1.5 py-0.5" style={{ background: 'var(--mv-scr)' }}>
                    ‹ Week 27
                  </span>
                  <span className="rounded-full px-1.5 py-0.5" style={{ background: 'var(--mv-scr)' }}>
                    Week 29 ›
                  </span>
                </div>
                <div className="grid grid-cols-7 gap-0.5 text-center">
                  {urenWeek(t).map((day, i) => {
                    const sel = i === UREN_SEL_DAY
                    return (
                      <span
                        key={day.n}
                        className="flex flex-col items-center gap-px rounded-[7px] py-1"
                        style={{ background: sel ? 'var(--mv-accent-soft)' : 'transparent' }}
                      >
                        <span className="text-[7.5px]" style={{ color: 'var(--mv-text2)' }}>
                          {day.d}
                        </span>
                        <span
                          className="text-[10px] font-bold tabular-nums"
                          style={{ color: sel ? 'var(--mv-accent)' : 'var(--mv-text)' }}
                        >
                          {day.n}
                        </span>
                        <span
                          className="text-[7px] tabular-nums"
                          style={{ color: sel ? 'var(--mv-accent)' : 'var(--mv-text2)' }}
                        >
                          {day.u}
                        </span>
                      </span>
                    )
                  })}
                </div>
                <div className="flex justify-between text-[7.5px]" style={{ color: 'var(--mv-text2)' }}>
                  <span>
                    Week 28 ·{' '}
                    <b className="tabular-nums" style={{ color: 'var(--mv-text)' }}>
                      40:00
                    </b>
                  </span>
                  <span>
                    {t.monthsFull[6]} ·{' '}
                    <b className="tabular-nums" style={{ color: 'var(--mv-accent)' }}>
                      {monthTotal}
                    </b>
                  </span>
                </div>
              </div>
              <div
                className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-hidden rounded-[11px] p-2"
                style={{ background: 'var(--mv-card)' }}
              >
                {days.map((day) => (
                  <div key={day.label} className="flex flex-col gap-1.5">
                    <span className="text-[8px] font-bold tracking-[0.04em]" style={{ color: 'var(--mv-text2)' }}>
                      {day.label}
                    </span>
                    {day.entries.map((en, i) => (
                      <div
                        key={i}
                        className={clsx('flex items-center gap-2 rounded-r-[7px] py-[3px] pr-1.5 pl-2', styles.tone)}
                        style={{
                          borderLeft: '2px solid var(--mv-accent)',
                          background: en.hl ? 'var(--mv-accent-soft)' : 'transparent',
                        }}
                      >
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-[9.5px] font-semibold">{en.klant}</span>
                          <span className="truncate text-[8px]" style={{ color: 'var(--mv-text2)' }}>
                            {en.act}
                          </span>
                        </div>
                        <span
                          className="shrink-0 rounded-[6px] px-1.5 py-0.5 text-[9px] font-bold tabular-nums"
                          style={{ background: 'var(--mv-accent-soft)', color: 'var(--mv-accent)' }}
                        >
                          {en.uur}
                        </span>
                      </div>
                    ))}
                    {day.empty && (
                      <span className="text-[8px]" style={{ color: 'var(--mv-text2)' }}>
                        {t.screen.mobile.noHoursToday}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Form: slides up */}
            <div
              className={clsx('absolute inset-0 flex flex-col gap-1.5 rounded-[14px] px-2 py-0.5', styles.slideY)}
              style={{
                background: 'var(--mv-scr)',
                opacity: formOpen ? 1 : 0,
                transform: formOpen ? 'translateY(0)' : 'translateY(100%)',
                pointerEvents: 'none',
              }}
            >
              <div className="flex flex-col gap-2.5 rounded-[12px] px-2 py-3" style={{ background: 'var(--mv-card)' }}>
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="flex size-[34px] items-center justify-center rounded-[10px]"
                    style={{ background: 'var(--mv-scr)' }}
                  >
                    <Icon size={16} strokeWidth={2} data-sentry-element="Icon" data-sentry-source-file="uren-phone.tsx">
                      <path d="M5 12h14" data-sentry-element="path" data-sentry-source-file="uren-phone.tsx" />
                    </Icon>
                  </span>
                  <b className="text-[34px] font-extrabold tabular-nums">{amount}</b>
                  <span
                    className="flex size-[34px] items-center justify-center rounded-[10px]"
                    style={{ background: 'var(--mv-scr)' }}
                  >
                    <Icon size={16} strokeWidth={2} data-sentry-element="Icon" data-sentry-source-file="uren-phone.tsx">
                      <path d="M12 5v14M5 12h14" data-sentry-element="path" data-sentry-source-file="uren-phone.tsx" />
                    </Icon>
                  </span>
                </div>
                <div className="flex gap-1">
                  {UREN_QUICK.map((q, i) => {
                    const hot = i === 1 && step === 7
                    return (
                      <span
                        key={q}
                        className={clsx('flex-1 rounded-[8px] py-[7px] text-center text-[9px] font-bold', styles.tone)}
                        style={{
                          background: hot ? 'var(--mv-accent)' : 'var(--mv-scr)',
                          color: hot ? '#fff' : 'var(--mv-text2)',
                        }}
                      >
                        {q}
                      </span>
                    )
                  })}
                </div>
              </div>
              <div className="flex flex-col rounded-[12px] px-2.5 py-0.5" style={{ background: 'var(--mv-card)' }}>
                <FormRow
                  icon={
                    <Icon size={13}>
                      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
                      <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
                    </Icon>
                  }
                  label={t.screen.mobile.date}
                  value={fmt.fullDate(BOOKING.day)}
                data-sentry-element="FormRow" data-sentry-source-file="uren-phone.tsx" />
                <FormRow
                  icon={briefcase}
                  label={t.word.client.toUpperCase()}
                  value={chosen ? client(BOOKING.nr).name : t.screen.mobile.chooseClient}
                  valueColor={chosen ? 'var(--mv-text)' : 'var(--mv-text2)'}
                  background={formOpen && !chosen ? 'var(--mv-accent-soft)' : undefined}
                data-sentry-element="FormRow" data-sentry-source-file="uren-phone.tsx" />
                <FormRow
                  icon={
                    <Icon size={13}>
                      <path d="M3.5 7a2 2 0 0 1 2-2h3.2l2 2.2H18.5a2 2 0 0 1 2 2v7.3a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2Z" />
                    </Icon>
                  }
                  label={t.word.project.toUpperCase()}
                  value={chosen ? CURRENT_YEAR : '—'}
                  valueColor={chosen ? 'var(--mv-text)' : 'var(--mv-text2)'}
                data-sentry-element="FormRow" data-sentry-source-file="uren-phone.tsx" />
                <FormRow
                  icon={
                    <Icon size={13}>
                      <path d="M4 4h6.5l9 9-6.5 6.5-9-9V4Z" />
                      <circle cx="8" cy="8" r="1.3" />
                    </Icon>
                  }
                  label={t.word.activity.toUpperCase()}
                  value={activityLabel(t, activity) ?? activity}
                  valueColor={step >= 6 ? 'var(--mv-text)' : 'var(--mv-text2)'}
                  background={step === 6 ? 'var(--mv-accent-soft)' : undefined}
                  last
                data-sentry-element="FormRow" data-sentry-source-file="uren-phone.tsx" />
              </div>
              <div
                className="flex items-center gap-2.5 rounded-[12px] px-2.5 py-[7px]"
                style={{ background: 'var(--mv-card)' }}
              >
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-full"
                  style={{ background: 'var(--mv-icon-bg)', color: 'var(--mv-text2)' }}
                >
                  <Icon size={13} data-sentry-element="Icon" data-sentry-source-file="uren-phone.tsx">
                    <circle cx="12" cy="8" r="3.5" data-sentry-element="circle" data-sentry-source-file="uren-phone.tsx" />
                    <path d="M5 20a7 7 0 0 1 14 0" data-sentry-element="path" data-sentry-source-file="uren-phone.tsx" />
                  </Icon>
                </span>
                <div className="flex flex-col">
                  <span className="text-[8px] font-bold tracking-[0.03em]" style={{ color: 'var(--mv-text2)' }}>
                    {t.screen.mobile.user}
                  </span>
                  <span className="text-[11px] font-semibold">{teamMember(HOURS_EMPLOYEE.member).name}</span>
                </div>
              </div>
            </div>
          </div>

          <TabBar activeIndex={2} data-sentry-element="TabBar" data-sentry-source-file="uren-phone.tsx" />
        </div>
      </PhoneFrame>
    </AppUiVisual>
  )
}

// ---------------------------------------------------------------------------------------------
// Klanten variant (Anim Mobiel soort="klanten") — client list → client detail. Rest step 8 = the
// Van Dijk Vastgoed detail.
// ---------------------------------------------------------------------------------------------

// The phone's clients list — names, numbers and legal forms from the shared client base, so it
// shows the same clients (and the same numbers) as the desktop lists.
